import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from "@angular/core";
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { ReponseBase } from './reponse-base';
import { ActiviteEntite } from '../entites/activite';
import { BaseService } from './base.service';
import { MessageService } from 'primeng/api';
import { ETypeLien } from '../entites/enums/type-lien-document';

@Injectable({ providedIn: 'root' })
export class ActiviteService extends BaseService {

  private baseUrl = environment.apiBaseUrl + 'activite';
  private obtenirListeUrl = this.baseUrl;
  private obtenirParEmployeUrl = this.baseUrl + '/employe/{employeId}';
  private obtenirParTypeUrl = this.baseUrl + '/type/{typeLienId}';
  private obtenirDetailUrl = this.baseUrl + '/{id}';
  private ajouterUrl = this.baseUrl;
  private modifierUrl = this.baseUrl + '/modifier';
  private supprimerUrl = this.baseUrl + '/{id}';
  private obtenirRecentesUrl = this.baseUrl + '/recentes'; 
  private filtrerUrl = this.baseUrl + '/typeLien/{typeLienId}/filtrer';

  public httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
  };

  constructor(
    private http: HttpClient,
    public router: Router,
    public messageService: MessageService
  ) {
    super(messageService, router);
  }

  /**
   * Gets all activities ordered by creation date (most recent first) for timeline display
   */
  public ObtenirListe(): Observable<ReponseBase<ActiviteEntite[]>> {
    return this.http.get<ReponseBase<ActiviteEntite[]>>(this.obtenirListeUrl)
      .pipe(
        catchError(error => this.handleError(error))
      );
  }

  /**
   * Gets activities for a specific employee
   * @param employeId The employee ID to filter by
   */
  public ObtenirParEmploye(employeId: number): Observable<ReponseBase<ActiviteEntite[]>> {
    const url = this.obtenirParEmployeUrl.replace('{employeId}', employeId.toString());
    return this.http.get<ReponseBase<ActiviteEntite[]>>(url)
      .pipe(
        catchError(error => this.handleError(error))
      );
  }

  /**
   * Gets activities by type (TypeLien)
   * @param typeLienId The type ID to filter by
   */
  public ObtenirParType(typeLienId: number): Observable<ReponseBase<ActiviteEntite[]>> {
    const url = this.obtenirParTypeUrl.replace('{typeLienId}', typeLienId.toString());
    return this.http.get<ReponseBase<ActiviteEntite[]>>(url)
      .pipe(
        catchError(error => this.handleError(error))
      );
  }

  /**
   * Gets a specific activity by ID
   * @param id The activity ID
   */
  public ObtenirDetailActivite(id: number): Observable<ReponseBase<ActiviteEntite>> {
    const url = this.obtenirDetailUrl.replace('{id}', id.toString());
    return this.http.get<ReponseBase<ActiviteEntite>>(url)
      .pipe(
        catchError(error => this.handleError(error))
      );
  }

  /**
   * Creates a new activity
   * @param activite The activity to create
   */
  public AjouterActivite(activite: ActiviteEntite): Observable<ReponseBase<ActiviteEntite>> {
    return this.http.post<ReponseBase<ActiviteEntite>>(this.ajouterUrl, activite, this.httpOptions)
      .pipe(
        catchError(error => this.handleError(error))
      );
  }

  /**
   * Updates an existing activity
   * @param activite The activity to update
   */
  public ModifierActivite(activite: ActiviteEntite): Observable<ReponseBase<ActiviteEntite>> {
    return this.http.post<ReponseBase<ActiviteEntite>>(this.modifierUrl, activite, this.httpOptions)
      .pipe(
        catchError(error => this.handleError(error))
      );
  }

  /**
   * Deletes an activity
   * @param id The activity ID to delete
   */
  public SupprimerActivite(id: number): Observable<ReponseBase<number>> {
    const url = this.supprimerUrl.replace('{id}', id.toString());
    return this.http.delete<ReponseBase<number>>(url)
      .pipe(
        catchError(error => this.handleError(error))
      );
  }

  /**
   * Gets recent activities (last 50) for quick timeline view
   */
  public ObtenirActivitesRecentes(): Observable<ReponseBase<ActiviteEntite[]>> {
    return this.http.get<ReponseBase<ActiviteEntite[]>>(this.obtenirRecentesUrl)
      .pipe(
        catchError(error => this.handleError(error))
      );
  }

  /**
   * Gets activities filtered by metadata fields using dynamic query parameters
   * @param filtres Object containing the filter parameters (e.g., { EmplacementId: 123, ItemId: 456 })
   * @param limit Maximum number of activities to return (default: 100)
   */
  public FiltrerParMetadata(typeLien: ETypeLien, filtres: { [key: string]: any } = {}, limit: number = 100): Observable<ReponseBase<ActiviteEntite[]>> {
    let queryParams = new URLSearchParams();
    
    // Add dynamic filter parameters
    Object.keys(filtres).forEach(key => {
      if (filtres[key] !== null && filtres[key] !== undefined && filtres[key] !== '') {
        queryParams.append(key, filtres[key].toString());
      }
    });
    
    // Add limit parameter
    queryParams.append('limit', limit.toString());
    
    const url = `${this.filtrerUrl.replace('{typeLienId}', typeLien.toString())}?${queryParams.toString()}`;
    
    return this.http.get<ReponseBase<ActiviteEntite[]>>(url)
      .pipe(
        catchError(error => this.handleError(error))
      );
  }
}